import { useState, type FormEvent } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Package, Hash, ChevronDown, ChevronUp, Loader2, Truck, ShieldAlert, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getOrderById } from '../../services/firestore/orderService';
import { OrderTimeline } from './OrderTimeline';
import type { Order, OrderStatus } from '../../types/order';
import { ORDER_PREFIX } from '../../config/brand';

// --- Status badge colors ---

const STATUS_COLORS: Record<OrderStatus, string> = {
  pending: 'bg-suboptimal/20 text-suboptimal',
  paid: 'bg-optimal/20 text-optimal',
  processing: 'bg-gold/20 text-gold',
  shipped: 'bg-accent-light/20 text-accent-light',
  delivered: 'bg-optimal/20 text-optimal',
  cancelled: 'bg-critical/20 text-critical',
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

// --- Order card ---

function OrderCard({ order }: { order: Order }) {
  const [expanded, setExpanded] = useState(true);

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="bg-bg-card border border-bg-tertiary rounded-xl overflow-hidden"
    >
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        className="w-full text-left p-5 hover:bg-bg-elevated/30 transition-colors"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 space-y-2">
            <p className="text-sm text-text-muted">
              Order{' '}
              <span className="font-mono text-text-secondary">{order.id}</span>
            </p>
            {order.bundleName && (
              <p className="text-base font-medium text-text-primary truncate">
                {order.bundleName}
              </p>
            )}
            <div className="flex items-center gap-4 text-sm">
              <span className="text-text-muted">{formatDate(order.createdAt)}</span>
              <span className="text-accent-light font-bold">
                ${(order.totalInCents / 100).toFixed(2)}
              </span>
            </div>
          </div>
          <div className="flex flex-col items-end gap-3 flex-shrink-0">
            <span
              className={`inline-block px-3 py-1 rounded-full text-xs font-semibold capitalize ${
                STATUS_COLORS[order.status] || 'bg-bg-tertiary text-text-muted'
              }`}
            >
              {order.status}
            </span>
            {expanded ? (
              <ChevronUp size={18} className="text-text-muted" />
            ) : (
              <ChevronDown size={18} className="text-text-muted" />
            )}
          </div>
        </div>
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5 border-t border-bg-tertiary pt-5 space-y-5">
              <OrderTimeline order={order} />

              {order.trackingNumber && (
                <div className="flex items-center gap-3 bg-bg-elevated/50 border border-bg-tertiary rounded-lg p-4">
                  <Truck size={20} className="text-accent-light flex-shrink-0" />
                  <div className="text-sm">
                    <p className="text-text-secondary font-medium">Tracking Number</p>
                    <p className="font-mono text-text-primary mt-0.5">{order.trackingNumber}</p>
                    {order.trackingCarrier && (
                      <p className="text-text-muted mt-0.5">via {order.trackingCarrier}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// --- Main component ---

export function OrderLookup() {
  const [orderId, setOrderId] = useState('');
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = orderId.trim().toUpperCase();
    if (!trimmed) return;

    setLoading(true);
    setError(null);
    setOrder(null);
    setSearched(false);

    try {
      const result = await getOrderById(trimmed);
      setOrder(result);
      setSearched(true);
    } catch {
      setError('Failed to look up order. Please check your order ID and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg-deep text-text-primary">
      <div className="max-w-[500px] mx-auto px-4 py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="text-center mb-10"
        >
          <Package className="w-12 h-12 text-gold mx-auto mb-4" />
          <h1 className="font-display text-2xl font-bold mb-2">Order Status</h1>
          <p className="text-text-secondary text-base">
            Enter your order ID to view status and tracking information.
          </p>
          <p className="text-text-muted text-sm mt-1">
            Your order ID was shown on your confirmation page (e.g. {ORDER_PREFIX}-XXXXXXXX).
          </p>
        </motion.div>

        {/* Lookup form */}
        <motion.form
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="mb-8"
        >
          <label htmlFor="order-id" className="sr-only">
            Order ID
          </label>
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Hash
                size={18}
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none"
              />
              <input
                id="order-id"
                type="text"
                required
                placeholder={`${ORDER_PREFIX}-XXXXXXXX`}
                value={orderId}
                onChange={(e) => setOrderId(e.target.value)}
                className="w-full bg-bg-card border border-bg-tertiary rounded-xl pl-11 pr-4 py-3.5 text-base text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent-light/50 focus:border-accent-light transition-colors font-mono uppercase"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 px-5 py-3.5 bg-accent-light text-white rounded-xl font-medium text-base hover:bg-accent-light/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
            >
              {loading ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Search size={18} />
              )}
              <span className="hidden sm:inline">Look Up</span>
            </button>
          </div>
        </motion.form>

        {/* Error state */}
        {error && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center text-critical text-base mb-6"
          >
            {error}
          </motion.div>
        )}

        {/* Loading state */}
        {loading && (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-accent-light mx-auto mb-4" />
            <p className="text-text-secondary text-base">Looking up your order...</p>
          </div>
        )}

        {/* No results */}
        {searched && !loading && !order && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-12"
          >
            <Package className="w-10 h-10 text-text-muted mx-auto mb-3" />
            <p className="text-text-secondary text-base">
              No order found with that ID.
            </p>
            <p className="text-text-muted text-sm mt-2">
              Double-check your order ID — it starts with {ORDER_PREFIX}- followed by 8 characters.
            </p>
          </motion.div>
        )}

        {/* Order result */}
        {order && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <OrderCard order={order} />
          </motion.div>
        )}

        {/* Adverse reaction support — Launch Blocker #13 */}
        <div className="mt-10 bg-red-50 border border-red-200 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-2">
            <ShieldAlert size={16} className="text-red-700 flex-shrink-0" />
            <h3 className="text-[14px] font-semibold text-red-900">Experiencing Side Effects?</h3>
          </div>
          <p className="text-[13px] text-zinc-700 leading-relaxed mb-3">
            If you are having a reaction to a supplement, <strong className="text-zinc-900">stop taking it immediately</strong> and
            contact your healthcare provider. Then let us know so we can help.
          </p>
          <div className="flex flex-col gap-2">
            <Link
              to="/support#adverse-reaction"
              className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-rose-600 hover:underline"
            >
              <ShieldAlert size={13} />
              Report an adverse reaction
            </Link>
            <a
              href="mailto:safety@hemologix.ai"
              className="inline-flex items-center gap-1.5 text-[13px] font-medium text-zinc-500 hover:text-zinc-700 hover:underline"
            >
              Or email safety@hemologix.ai directly
            </a>
            <a
              href="https://www.safetyreporting.hhs.gov"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-[13px] font-medium text-zinc-400 hover:text-zinc-600 hover:underline"
            >
              <ExternalLink size={12} />
              File with FDA MedWatch
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
